<template>
  <div class="header-wrapper">
    <div class="banner-slot">
      <Banner />
      <CartReminderBanner />
    </div>

    <div class="header" :class="{ 'header--mega-nav-open': isMegaNavOpen }">
      <div class="header__left">
        <IconButton
          v-if="!mq.xlPlus"
          form="transparent"
          class="header__icon header__burger-left"
          data-testid="burger-menu"
          icon="burger"
          @click="toggleMobileMenu()"
        />
        <a href="/">
          <img src="@/assets/images/logo.svg" class="header__logo" />
        </a>
      </div>

      <div class="header__middle">
        <ul class="nav">
          <li
            v-for="item in navigation.items"
            :key="item.slug"
            class="nav__item"
          >
            <a
              form="underline-invert"
              :class="getLinkClass(item)"
              :href="item.url"
              @mouseover="openMegaNav(item.slug)"
              @mouseleave="delayedCloseMegaNav"
            >
              {{ item.title }}
            </a>
          </li>
        </ul>
      </div>

      <div class="header__right">
        <IconButton
          form="transparent"
          class="header__icon"
          data-testid="search"
          icon="search-new"
          @click="setSearchOpen(true)"
        />
        <button
          type="button"
          class="header__icon header__wishlist"
          aria-label="Wishlist"
          @click="handleWishlistClick"
        >
          <img :src="wishlistIconSrc" alt="" class="header__wishlist-icon" />
        </button>
        <IconButton
          form="transparent"
          :tag="isLoggedIn ? 'a' : 'button'"
          class="header__icon"
          icon="account-new"
          :href="isLoggedIn ? accountUrl : ''"
          @click="!isLoggedIn && setAccountOpen(true)"
        />
        <!-- <IconButton
          form="transparent"
          :tag="isLoggedIn ? 'a' : 'button'"
          class="header__icon"
          icon="star-new"
          :href="isLoggedIn ? accountUrl : ''"
          @click="!isLoggedIn && setAccountOpen(true)"
        /> -->
        <IconButton
          form="transparent"
          class="header__icon"
          icon="bag-new"
          :notification-number="lineItemsCount"
          @click="setCartOpen(true)"
        />
      </div>
    </div>

    <DesktopMegaNav />
  </div>

  <Transition name="fade">
    <div v-if="isBurgerMenuOpen" class="blur" @click="toggleMobileMenu"></div>
  </Transition>
  <Transition name="slide-in-left">
    <BurgerMenu v-if="isBurgerMenuOpen" />
  </Transition>
  <BagV2 />
  <SearchNavigation />
  <LoginPanel />
  <DesktopNavigation v-if="mq.lgPlus" />
</template>

<script>
import IconButton from "@/components/IconButton.vue";
import Banner from "@/components/Banner.vue";
import CartReminderBanner from "@/components/cart/CartReminderBanner.vue";
import BagV2 from "@/components/BagV2.vue";
import SearchNavigation from "@/components/SearchNavigation.vue";
import LoginPanel from "@/components/LoginPanel.vue";
import DesktopNavigation from "@/components/navigation/DesktopNavigation.vue";
import DesktopMegaNav from "@/components/navigation/DesktopMegaNav.vue";
import BurgerMenu from "@/components/BurgerMenu.vue";
import { mapActions, mapState, mapGetters } from "vuex";
import bookmarkOutlineIcon from "@/assets/icons/bookmark.png";
import bookmarkBlackIcon from "@/assets/icons/bookmark-black.png";
// import navigationContent from "@/content/navigation.json";

export default {
  name: "HeaderComponent",

  components: {
    IconButton,
    BagV2,
    BurgerMenu,
    LoginPanel,
    Banner,
    CartReminderBanner,
    DesktopNavigation,
    SearchNavigation,
    DesktopMegaNav,
  },
  inject: ["mq"],
  props: {
    version: {
      type: String,
      default: "2",
    },
  },
  data() {
    return {
      bookmarkOutlineIcon,
      bookmarkBlackIcon,
    };
  },
  computed: {
    ...mapState("app", ["isBurgerMenuOpen", "isMegaNavOpen", "megaNavMenu"]),
    ...mapState("auth", ["multipassAccount"]),
    ...mapGetters("auth", ["isLoggedIn"]),
    ...mapGetters("cart", ["lineItemsCount"]),
    accountUrl() {
      return this.multipassAccount.url || "/account/login";
    },
    isTransparentHeaderPage() {
      const appClasses = document.querySelector("#app")?.classList;
      return (
        appClasses?.contains("template-index") ||
        appClasses?.contains("template-page-brand-world")
      );
    },
    navigation() {
      try {
        return require(`@/content/navigationV${this.version}.json`);
      } catch (e) {
        console.error(e);
        return require(`@/content/navigation.json`);
      }
    },
    wishlistIconSrc() {
      // On home/template-index with transparent header, use white icon;
      // otherwise (scrolled, mega nav open, or other pages) use black icon.
      if (this.isTransparentHeaderPage && !this.isMegaNavOpen) {
        return this.bookmarkOutlineIcon;
      }
      return this.bookmarkBlackIcon;
    },
  },
  mounted() {
    this.setNavVersion(this.version);
  },
  methods: {
    ...mapActions("app", [
      "toggleMobileMenu",
      "setAccountOpen",
      "setWishlistLoginOpenState",
      "setNavOpen",
      "openMegaNav",
      "delayedCloseMegaNav",
      "setNavVersion",
    ]),
    ...mapActions("search", ["setSearchOpen"]),
    ...mapActions("cart", ["setCartOpen"]),
    handleWishlistClick() {
      if (!this.isLoggedIn) {
        this.setWishlistLoginOpenState(true);
        return;
      }

      window.location.href = "/account#/wishlist";
    },
    hasChildLinks(item) {
      if (item.mega?.columns.length > 0) {
        return true;
      }
      return false;
    },
    getLinkClass(item) {
      const baseClass = "nav__link";
      let classes = [baseClass];

      if (item.modifier) {
        classes.push(`${baseClass}--${item.modifier}`);
      }

      // Add active class if this item's mega nav is currently open
      if (this.isMegaNavOpen && this.megaNavMenu === item.slug) {
        classes.push(`${baseClass}--active`);
      }

      // Keep flagged items (e.g. SALE) red at all times, whether the mega
      // nav is open or closed, and on any template.
      if (item.highlightRed === true) {
        classes.push(`${baseClass}--highlight`);
      }

      if (item.italic) {
        classes.push(`${baseClass}--italic`);
      }

      if (this.isMembersItem(item)) {
        classes.push(`${baseClass}--members`);
      }

      return classes.join(" ");
    },
    isMembersItem(item) {
      return item?.slug === "members" || item?.title === "Members";
    },
  },
};
</script>

<style lang="scss" scoped>
.banner-slot {
  position: relative;
  overflow: hidden;
  // Keep above DesktopMegaNav .blur (z-index: 3) so banner copy stays visible
  z-index: 5;
  // The banner renders once shop info has loaded; reserve its height so the
  // header (and the page padding under the fixed header) does not jump down
  // by the banner height on every page load.
  min-height: 34px;

  @include respond-above(md) {
    min-height: 36px;
  }

  :deep(.banner) {
    position: relative;
    z-index: 1;
  }
}

.header {
  background-color: $color-white;
  display: flex;
  align-items: center;
  gap: 40px;
  padding: 8px 20px;
  align-items: stretch;

  @include respond-above(xl) {
    position: relative;
    padding: 0 40px;
    height: 92px;
  }

  &__middle {
    display: none;

    @include respond-above(lg) {
      display: flex;
      align-items: center;
      flex: 1 0 auto;
      justify-content: center;
    }
  }

  &__left,
  &__right {
    flex: 1 0 auto;

    @include respond-above(xl) {
      flex: 0 1 auto;
    }
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__right {
    display: flex;
    justify-content: flex-end;
    text-align: right;
    margin-left: auto;
    align-self: center;

    @include respond-above(xl) {
      justify-content: flex-end;
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    font-size: 24px;
    line-height: 1;
    padding: 8px;

    &:last-child {
      margin-right: -7px;
    }

    @include respond-above(xl) {
      padding: 10px;
    }

    @include respond-above(sm) {
      padding: 12px;
    }
  }

  &__wishlist {
    background: transparent;
    border: none;
    cursor: pointer;
    outline: none;
    transition: opacity 0.2s ease;

    &:focus-visible {
      outline: none;
    }

    &:hover {
      opacity: 0.7;
    }
  }

  &__wishlist-icon {
    width: 10px;
    height: 14px;
    display: block;
    border: 0;
  }

  :deep(.button--icon--has-text .icon) {
    @include respond-below(xl) {
      margin-right: 0;
    }
  }

  &__logo {
    vertical-align: middle;
    width: 141px;

    @include respond-above(xl) {
      width: 205px;
    }
  }

  &__burger-left {
    margin-left: -8px;

    @include respond-above(sm) {
      margin-left: -10px;
    }
  }

  &__text {
    @include t__utility-label-20;
  }

  &--mega-nav-open {
    box-shadow: inset 0px -1px #e4e4e4;
  }
}

.template-index .header,
.template-page-brand-world .header {
  transition: 0.25s ease background;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.6) 0%,
    rgba(0, 0, 0, 0) 100%
  );

  * {
    transition: 0.2s;
  }

  *,
  :deep(button),
  :deep(.icon-button__notification) {
    color: $color-white !important;
  }

  .header__logo {
    filter: invert(1);
  }

  &--mega-nav-open {
    background: $color-white;

    .header__logo {
      filter: invert(0);
    }

    *,
    :deep(button) {
      color: $color-black !important;
    }

    .nav__link--members {
      color: $color-members-green !important;
    }
  }
}

.nav {
  display: flex;
  gap: 20px;
  align-items: stretch;
  height: 100%;

  &__item {
    display: flex;
    align-items: center;
  }

  &__link {
    display: flex;
    align-items: center;
    height: 100%;

    @include editorial-body(14px);
    text-transform: none;
    text-decoration: none;
    border-bottom: 1px solid transparent;

    &.nav__link--highlight {
      color: $color-red !important;
    }

    &.nav__link--italic {
      font-style: italic;
    }

    &.nav__link--members {
      color: $color-members-green;
    }

    &:hover,
    &.nav__link--active {
      border-bottom: 1px solid black;
    }
  }
}

.slide-in-left-enter-active,
.slide-in-left-leave-active {
  transition: transform 0.25s ease-out, opacity 0.25s ease-out;
  opacity: 1;
}

.slide-in-left-enter-from,
.slide-in-left-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.blur {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 4;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur($blur-amount);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<style lang="scss">
.fixed-header {
  padding-top: 86px;

  @include respond-above(lg) {
    padding-top: 96px;
  }

  @include respond-above(xl) {
    padding-top: 128px;
  }

  &.template-index,
  &.template-page-brand-world {
    padding-top: 34px;

    @include respond-above(xl) {
      padding-top: 36px;
    }
  }

  .header-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 4;
  }

  .header__icon.icon-button--has-text .icon {
    @include respond-below(md) {
      margin-right: 0 !important;
    }
  }
}
</style>
